<script lang="ts">
  import { countBy, groupBy, max, maxBy, sumBy } from 'lodash-es'
  import { scaleLinear, scaleSqrt } from 'd3'

  export let dataset = []
  export let groupByCategory: string = ''
  let svgElement
  let innerWidth
  let innerHeight
  let hoveredBubbleIndex
  let hoveredGroupIndex

  let mouseCoordinates = { x: 0, y: 0 }
  function handleMousemove(event) {
    mouseCoordinates.x = event.clientX
    mouseCoordinates.y = event.clientY
  }

  let currentTooltipDatum = null

  $: svgWidth = innerWidth * 0 + svgElement?.getBoundingClientRect()?.width || 10

  $: isInsideWindowY = innerHeight - mouseCoordinates.y >= 200 ? true : false
  $: isInsideWindowX = innerWidth - mouseCoordinates.x >= 200 ? true : false

  $: xScale = scaleLinear()
    .domain([0, 1000]) // km
    .range([0, svgWidth]) // px

  function groupByObject(array, field) {
    return Object.entries(groupBy(array, field)).map(([label, values]) => ({
      label,
      values,
    }))
  }

  $: formattedDataset = groupByObject(
    dataset
      .filter((userAnswers) => userAnswers[groupByCategory] !== undefined)
      .map((userAnswersFiltered) => {
        return {
          distance: parseInt(userAnswersFiltered.mean_distance),
          groupByCategory: userAnswersFiltered[groupByCategory],
        }
      }),
    'groupByCategory'
  ).map((group) => ({
    label: group.label,
    countByDistance: Object.entries(countBy(group.values, 'distance')).map(([distance, count]) => ({
      distance: Number(distance),
      count,
    })),
  }))

  $: maxCount =
    maxBy<any>(
      formattedDataset.flatMap((g) => g.countByDistance),
      'count'
    )?.count || 0

  $: rScale = scaleSqrt().domain([0, maxCount]).range([0, 50])
</script>

<svelte:window bind:innerWidth bind:innerHeight />

<div class="chart">
  {#if currentTooltipDatum !== null}
    <div
      class="tooltip"
      style={`top: ${isInsideWindowY ? mouseCoordinates.y + 'px' : 'unset'}; bottom: ${
        isInsideWindowY ? 'unset' : innerHeight - mouseCoordinates.y + 'px'
      }; left: ${isInsideWindowX ? mouseCoordinates.x + 'px' : 'unset'}; right: ${
        isInsideWindowX ? 'unset' : innerWidth - mouseCoordinates.x + 'px'
      };`}
    >
      <div class="tooltip-row">
        <span>Totale</span>
        <p>{currentTooltipDatum.count}</p>
      </div>
      <div class="tooltip-row">
        <span>Tempo impiegato</span>
        <p>{currentTooltipDatum.label}</p>
      </div>
      <div class="tooltip-row">
        <span>Distanza</span>
        <p>{currentTooltipDatum.distance}km</p>
      </div>
    </div>
  {/if}

  {#each formattedDataset as lineGroup, groupIndex}
    <div class="line-group">
      <span class="line-label">{lineGroup.label}</span>
      <div class="chart-container">
        <span>0km</span>
        <svg bind:this={svgElement} height="100" width="100%">
          <rect y="50" width="100%" height="1" fill="rgba(255, 255, 255, 0.5)" />
          {#each lineGroup.countByDistance as { distance, count }, bubbleIndex}
            <circle
              fill={hoveredBubbleIndex === bubbleIndex && hoveredGroupIndex === groupIndex
                ? 'rgba(0, 255, 240, 0.9)'
                : 'rgba(0, 255, 240, 0.6)'}
              on:mouseover={() => {
                currentTooltipDatum = { label: lineGroup.label, distance, count }
                hoveredBubbleIndex = bubbleIndex
                hoveredGroupIndex = groupIndex
              }}
              on:focus={() => {}}
              on:mouseout={() => {
                currentTooltipDatum = null
                hoveredBubbleIndex = null
                hoveredGroupIndex = null
              }}
              on:blur={() => {}}
              on:mousemove={handleMousemove}
              cx={xScale(distance)}
              cy="50"
              r={rScale(count)}
            />
          {/each}
        </svg>
        <span>1000km</span>
      </div>
    </div>
  {:else}
    <div style="color: grey">Dati assenti</div>
  {/each}
</div>

<style>
  .chart {
    margin-top: 20px;
  }

  svg {
    overflow: visible;
  }

  .line-group {
    display: flex;
    align-items: center;
    gap: 2rem;
  }

  .line-label {
    flex: 0 0 120px;
    font-size: 12px;
  }

  .chart-container {
    flex: 1 1 100%;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 12px;
  }

  .tooltip {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 200px;
    height: 200px;
    position: fixed;
    top: calc(50vh - 100px);
    left: calc(50vw - 100px);
    color: black;
    background-color: white;
    padding: 1rem;
    border-radius: 10px;
  }

  .tooltip-row {
    display: flex;
    flex-direction: column;
    margin: 0.4rem 0;
  }

  .tooltip-row span {
    color: rgba(124, 124, 124, 1);
    font-size: 12px;
  }
  .tooltip-row p {
    font-size: 14px;
    font-weight: 600;
    margin: 0.1rem;
  }

  @media (max-width: 800px) {
    .line-group {
      flex-direction: column;
      align-items: stretch;
      gap: 1rem;
      margin-top: 2rem;
    }

    .line-group > span {
      width: initial;
      flex: 0 1 auto;
    }
  }
</style>
